import { catchError, switchMap, withLatestFrom, filter, delay, map, tap, mergeMap } from 'rxjs/operators';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Injectable, inject } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { from, Observable, of } from 'rxjs';

import { observeComputedFeature, observeFeature } from '../../../factor-features-module/stores/factor-features';
import { DunningActionsSpecsService } from '../../../dunnings-module/services/dunning-actions-specs.service';
import { DunningActionsService } from '../../../dunnings-module/services/dunning-actions.service';
import { DunningTimelineStore } from '../../../dunnings-module/stores/dunning-timeline/dunning-timeline.store';
import { DocumentFileService } from '../../../shared-module/services/document-file.service';
import { IDunningActionAttachmentDocument } from '../../../dunnings-module/models';
import * as AppStoreActions from '../../app-store/actions';
import { AppConstants } from '../../../app.constants';
import * as featureSelectors from '../selectors';
import * as featureActions from '../actions';
import { DocumentType } from '../../../shared-module/enums';

@Injectable({
	providedIn: 'root'
})
export class DunningActionDetailStoreAttachmentsEffects {

	private documentFileService = inject(DocumentFileService);
	private dunningActionsService = inject(DunningActionsService);
	private dasService = inject(DunningActionsSpecsService);
	private tlStore = inject(DunningTimelineStore);
	private actions$ = inject(Actions);
	private store = inject(Store);

	attachmentsRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadAttachmentsRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectSuperDebtorId),
			this.store.select(featureSelectors.selectDunningAction),
			this.store.select(featureSelectors.selectAttachments),
			this.store.select(featureSelectors.selectSelectedAtachmentList),
			this.store.select(featureSelectors.selectIsCompletionAccountStatementSelected),
			this.store.select(featureSelectors.selectIsInitComplete),
			this.store.select(featureSelectors.selectIsCompletionMode),
			observeComputedFeature<boolean>('hasBankStatement'),
			observeFeature('isChorusProEnabled'),
			observeFeature('isInterestPenaltyEnable'),
			this.dasService.getSpecsStream()),
		switchMap(([action, sid, dunningAction, allAttachments, selectedAttachments, asSelected, isInitComplete, _isCompletionMode, bankStatement, chorusStatement, interestPenaltyStatement, specs]) => {
			const isRealized = !!dunningAction.realizationDate;
			const attachmentsSource = this.dunningActionsService.getActionAttachments({
				dunningActionId: action.id,
				payingCenterModeEnabled: false,
				withActionAccountingDocumentsFiles: !isRealized,
				selectAccountingManagedDocumentsFiles: true,
				withSuperDebtorAccountingFiles: dunningAction.accounting,
				withSuperDebtorDocumentsFiles: !isRealized ? sid : undefined,
				selectAccountingDocumentsFiles: !isRealized && dunningAction.accountingDocumentIncluded,
			});

			// some attachments are only known locally, keep them to reinject theme later
			const localAttachments = selectedAttachments.filter(a => a.document?.documentType === DocumentType.DunningActionDocumentToUpload);

			return attachmentsSource.pipe(
				delay(1),
				switchMap((remote: IDunningActionAttachmentDocument[]) => {
					const list = [...remote,...localAttachments];
					return [
						featureActions.LoadAttachmentsSuccess({entity: list, correlationParams: action.correlationParams}), // LoadAttachmentsSuccess should be aggregatable
						// after an upload, current selection should be preserved
						!isInitComplete ? AppStoreActions.Noop() : featureActions.UnselectDocuments({param: allAttachments}),
						!isInitComplete ? AppStoreActions.Noop() : featureActions.SelectDocuments({param: selectedAttachments}),

						// in some cases, upon initialization, all attachments should be pre-selected
						(!isInitComplete && specs[dunningAction.type].startsWithAllAttachmentsSelected) ?
							featureActions.SelectDocuments({param: list.filter(a => a.selected)}) :
							AppStoreActions.Noop(),
						// AccountStatement should be loaded on the end if needed
						featureActions.InsertAttachmentPlaceholders(asSelected, { bankStatement, chorusStatement, interestPenaltyStatement }),

					]
				}),
				catchError(error =>
					of(featureActions.LoadAttachmentsFailure({error}))
				)
			);
		})
	));

	addAttachmentsRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.AddAttachmentsRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectIsEditMode),
			this.store.select(featureSelectors.selectIsCompletionMode),
			this.store.select(featureSelectors.selectAttachments),
			observeComputedFeature<boolean>('hasBankStatement'),
			observeFeature('isChorusProEnabled'),
			observeFeature('isInterestPenaltyEnable'),
		),
		filter(([, isEditMode, isCompletionMode]) => !isEditMode && !isCompletionMode),
		switchMap(([action, _, __, attachments, bankStatement, chorusStatement, interestPenaltyStatement]) => this.documentFileService.checkFile(action.param.file,
			AppConstants.FILE_UPLOADER_CONFIGS.ATTACHMENT,
			attachments.map(a => a.document.name),
			'Dunning.AttachmentFileToasts').pipe(
			switchMap(_ => [
				featureActions.AddAttachmentsSuccess({param: action.param}),
				featureActions.InsertAttachmentPlaceholders(false, { bankStatement, chorusStatement, interestPenaltyStatement }),
			]),
			catchError(error =>
				of(featureActions.AddAttachmentsFailure({param: error}))
			)
		))
	));

	addAttachmentsFailureEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.AddAttachmentsFailure),
		switchMap(action =>
			of(AppStoreActions.ToastError(action.param))
		)
	));

	updateAttachmentsRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.AddAttachmentsRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectIsEditMode),
			this.store.select(featureSelectors.selectIsCompletionMode),
			this.store.select(featureSelectors.selectAttachments),
			this.store.select(featureSelectors.selectDunningAction)),
		filter(([, isEditMode, isCompletionMode]) => isEditMode || isCompletionMode),
		switchMap(([action, _, __, attachments, dunningAction]) =>
			this.documentFileService.checkFile(action.param.file,
				AppConstants.FILE_UPLOADER_CONFIGS.ATTACHMENT,
				attachments.map(a => a.document.name),
				'Dunning.AttachmentFileToasts'
			).pipe(
				switchMap(() => this.dunningActionsService.uploadAttachedDocuments(
					dunningAction.id,
					action.param.file
				).pipe(
					switchMap(() => [
						featureActions.LoadAttachmentsRequest(dunningAction.id),
						AppStoreActions.Execute(() => this.tlStore.updateActionProps({
							dunningActionId: dunningAction.id,
							updater: _ => ({
								id: dunningAction.id,
								attachmentCount: attachments.length + 1
							})
						}))
					]))
				),
				catchError(error => of(featureActions.AddAttachmentsFailure({param: error}))))
		)
	));

	deleteAttachmentAction$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.DeleteAttachmentRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectAttachments),
			this.store.select(featureSelectors.selectDunningAction)),
		switchMap(([action, attachments, dunningAction]) => {
			const remoteFile = (action.param.document && action.param.document.id) ? action.param.document : null;
			if (remoteFile) {
				return this.dunningActionsService.deleteActionAttachments(dunningAction.id, remoteFile.id)
					.pipe(
						switchMap(_ => [
							featureActions.DeleteAttachmentSuccess({param: action.param}),
							AppStoreActions.Execute(() => this.tlStore.updateActionProps({
								dunningActionId: dunningAction.id,
								updater: _ => ({
									id: dunningAction.id,
									attachmentCount: attachments.length - 1
								})
							})),
						]),
						catchError(error => of(featureActions.DeleteAttachmentFailure({param: error})))
					);
			} else {
				return of(featureActions.DeleteAttachmentSuccess({param: action.param}));
			}
		})
	));

	selectSDAttachmentsEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SelectDocuments, featureActions.UnselectDocuments),
		filter(action => action.param.length === 1), // this only works for single-document actions
		tap(action => {
			const file = action.param.find(d => d.document.id > 0 && !d.isAccountingDoc);
			if (!file) return;

			if (action.type === featureActions.SelectDocuments.type && !file.selected)
				this.store.dispatch(featureActions.AttachDocument({ param: file }));

			if (action.type === featureActions.UnselectDocuments.type && file.selected)
				this.store.dispatch(featureActions.DetachDocument({ param: file }));

		}),
		map(() => AppStoreActions.Noop())
	));

	attachSDAttachmentsEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.AttachDocument, featureActions.DetachDocument),
		withLatestFrom(
			this.store.select(featureSelectors.selectDunningAction),
			this.store.select(featureSelectors.selectAttachments),
			this.store.select(featureSelectors.selectSelectedAtachmentList),
		),
		map(([action, dunningAction, allAttachments, selectedAttachments]) => ({
			method: (action.type === featureActions.AttachDocument.type
				? this.dunningActionsService.attachDocumentFileToAction.bind(this.dunningActionsService)
				: this.dunningActionsService.detachDocumentFileFromAction.bind(this.dunningActionsService)
			) as (id: number, d: IDunningActionAttachmentDocument) => Observable<void>,
			dunningAction,
			action,
			allAttachments,
			selectedAttachments,
		})),
		filter(({ dunningAction }) => !dunningAction.realizationDate && !!dunningAction.id),
		mergeMap(({ dunningAction, action, method, allAttachments, selectedAttachments }) => method(dunningAction.id, action.param).pipe(
			map(_ => AppStoreActions.Noop()),
			catchError(_ => from([
				// after an upload, current selection should be preserved
				featureActions.UnselectDocuments({param: allAttachments}),
				featureActions.SelectDocuments({param: selectedAttachments.filter(a => a.id !== action.param.id)}),
				featureActions.LoadAttachmentsRequest(dunningAction.id),
			])),
		))
	));
}
